// 佐川「周南」営業所 特殊計上ロジック（Issue #3）
//
// ★ 2026-06-08 実データ確定：本城さんから受領した4月確定通知書（吉岡里紗様）で
//    計算式を1円まで再現できることを検証済み（src/lib/calc/sagawaShunan.test.ts）。
//    旧実装（単価55/44/88/33・宅急便=宅配便種のみ）は誤りだったため全面訂正。
//
// 通常の佐川は「実績個数」で計上するが、周南営業所だけは件数ベースの特殊計上を行う。
//
// ■ 単価（周南固有・固定。4月通知書で確定）
//   複数口         = 50 円/個
//   メール         = 40 円/個
//   商業貨物       = 80 円/件
//   商業貨物複数口 = 30 円/個
//   宅急便         = マスター単価（driver.unitTakkyu）をそのまま使う
//
// ■ 計上ロジック（便種コードで判定。商業貨物は宅急便に含めない別集計）
//   宅急便系（便種コード 001 一般宅配 / 006 代引 / 009 着払 / 007 クール / 061 集荷）:
//     宅急便   = 実績件数の合計        （※通常は実績個数だが、周南は件数。クール・集荷も含む）
//     複数口   = (実績個数 − 実績件数) の合計（単価 50）
//   メール系（便種コード 004）:
//     メール   = 実績個数（単価 40）
//   商業貨物系（便種コード 008）:
//     商業貨物       = 実績件数        （単価 80）
//     商業貨物複数口 = 実績個数 − 実績件数（単価 30）
//   月額品目（104 荷捌 / 105 車両維持）等は計上対象外。
//
// 検証: 吉岡里紗4月 = 宅急便1229×150 + 複数口22×50 + 商業6×80 = 185,930円（通知書と一致）
//
// ★ 2026-08-07 本城さん指摘：売上明細表の日付が全員7/31（月末）になっていた。
//    周南だけ月次合算して月末1行に寄せていたため（他の業者・営業所は日別に出る）。
//    「金額は月次合算が正」を守ったまま、明細表示だけを日別に変更した。
//    日別に割った数量の合計が月次合計と一致するときだけ日別で出し、
//    一致しないときは従来どおり月末1行にまとめる（表示のために金額を動かさない）。

import type { Driver, SagawaRow, Statement, ItemRow, SagawaBenkindRule } from "../../types";
import { buildHeader, buildDeductions, summarize } from "./common";
import { classifySagawaCode, DEFAULT_SAGAWA_BENKIND_RULES } from "./sagawaRules";

// 周南固有の固定単価（4月確定通知書で確定）
export const SHUNAN_UNIT = {
  fukusuguchi: 50, // 複数口
  mail: 40, // メール
  shogyo: 80, // 商業貨物
  shogyoFukusu: 30, // 商業貨物複数口
} as const;

// 周南の識別：営業店名に「周南」を含むか
export function isShunanRow(row: SagawaRow): boolean {
  // 旧localStorageデータは office を持たない場合があるためガード
  return (row.office || "").includes("周南");
}

// 便種の分類（便種コードベース）
//   商業貨物 = 008（周南固有の別単価）／ メール = 004 は周南でも特別扱いで先に確定。
//   それ以外は便種マッピング（#32）に従い、takkyu→宅急便・nekopos→メール・other/ignore/未知→除外。
//   ※ デフォルトルールでは 001/006/009/007/061 が takkyu のため、従来の周南挙動を完全再現する。
// グループ集計（groupSum.ts）でも周南の便種分類をそのまま再利用するため export する。
export function classifyShunanBenkind(
  benkindCode: string,
  rules: SagawaBenkindRule[]
): "shogyo" | "mail" | "takkyu" | "ignore" {
  if (benkindCode === "008") return "shogyo";
  if (benkindCode === "004") return "mail";
  const cat = classifySagawaCode(benkindCode, rules);
  if (cat === "takkyu") return "takkyu";
  if (cat === "nekopos") return "mail";
  return "ignore";
}

// 区分ごとの件数・個数（1日ぶん or 月ぶん）
type ShunanAgg = { takkyuKensu: number; takkyuQty: number; mailQty: number; shogyoKensu: number; shogyoQty: number };
const emptyAgg = (): ShunanAgg => ({ takkyuKensu: 0, takkyuQty: 0, mailQty: 0, shogyoKensu: 0, shogyoQty: 0 });

function addRow(agg: ShunanAgg, row: SagawaRow, rules: SagawaBenkindRule[]): void {
  const cat = classifyShunanBenkind(row.benkindCode, rules);
  if (cat === "takkyu") {
    agg.takkyuKensu += row.countItems;
    agg.takkyuQty += row.countQty;
  } else if (cat === "mail") {
    agg.mailQty += row.countQty;
  } else if (cat === "shogyo") {
    agg.shogyoKensu += row.countItems;
    agg.shogyoQty += row.countQty;
  }
  // ignore はスキップ
}

// 区分 → 数量。複数口系は「個数 − 件数」（負にはしない）
function quantities(a: ShunanAgg): Record<string, number> {
  return {
    宅配: a.takkyuKensu,
    複数口: Math.max(0, a.takkyuQty - a.takkyuKensu),
    メール便: a.mailQty,
    商業貨物: a.shogyoKensu,
    商業貨物複数口: Math.max(0, a.shogyoQty - a.shogyoKensu),
  };
}

export function calcSagawaShunanStatement(
  driver: Driver,
  rows: SagawaRow[],
  targetMonth: string,
  rules: SagawaBenkindRule[] = DEFAULT_SAGAWA_BENKIND_RULES
): Statement {
  const warnings: string[] = [];

  // 月次合算（金額の正）と、日別集計（明細表の表示用）を同時に作る。
  // 周南は「複数口 = 個数 − 件数」を月合計で出すのが正なので、日別に割った合計が
  // 月次合計と一致するときだけ日別で出す（＝表示だけを変え、金額は絶対に変えない）。
  const monthAgg = emptyAgg();
  const byDate = new Map<string, ShunanAgg>();

  for (const row of rows) {
    if (!row.workDate.startsWith(targetMonth)) continue;
    addRow(monthAgg, row, rules);
    let day = byDate.get(row.workDate);
    if (!day) {
      day = emptyAgg();
      byDate.set(row.workDate, day);
    }
    addRow(day, row, rules);
  }

  const uTakkyu = driver.unitTakkyu ?? 0;
  if (uTakkyu === 0) warnings.push("マスター単価/宅急便 が未設定");

  const header = buildHeader(driver, ["sagawa"], targetMonth);
  const monthEnd = header.salesDate; // 売上計上日（月末）

  const unitOf: Record<string, number> = {
    宅配: uTakkyu, // 宅急便系（#16で項目名「宅急便」→「宅配」に統一）
    複数口: SHUNAN_UNIT.fukusuguchi,
    メール便: SHUNAN_UNIT.mail, // メール系（#16で「メール」→「メール便」に統一）
    商業貨物: SHUNAN_UNIT.shogyo, // 商業貨物系（宅急便には含めない・名称は据え置き）
    商業貨物複数口: SHUNAN_UNIT.shogyoFukusu,
  };
  const KINDS = ["宅配", "複数口", "メール便", "商業貨物", "商業貨物複数口"] as const;

  const monthQty = quantities(monthAgg);
  const dates = [...byDate.keys()].sort();

  // 日別に割ったときの区分ごとの合計
  const dailyTotal: Record<string, number> = { 宅配: 0, 複数口: 0, メール便: 0, 商業貨物: 0, 商業貨物複数口: 0 };
  for (const d of dates) {
    const q = quantities(byDate.get(d)!);
    for (const k of KINDS) dailyTotal[k] += q[k];
  }
  // 日別と月次で数量が一致するか（複数口は「個数 − 件数」を日ごとに切り上げるため理論上ズレうる）。
  // 実データ（周南6名・106日分）では全員一致することを確認済みだが、将来データでズレたら
  // 表示のために金額を変えるわけにはいかないので、従来どおり月末1行にまとめる。
  const daily0k = KINDS.every((k) => dailyTotal[k] === monthQty[k]);

  const items: ItemRow[] = [];
  if (daily0k && dates.length > 0) {
    for (const d of dates) {
      const q = quantities(byDate.get(d)!);
      for (const k of KINDS) {
        if (q[k] > 0) items.push(mkItem(d, k, unitOf[k], q[k]));
      }
    }
    // 月内に一度も発生しなかった区分は、従来どおり月末に数量0の行を出す
    // （支払通知書は品目ごとに集約表示するため、これで見た目が変わらない）
    for (const k of KINDS) {
      if (monthQty[k] === 0) items.push(mkItem(monthEnd, k, unitOf[k], 0));
    }
  } else {
    if (!daily0k) warnings.push("日別に分けると数量が合わないため、月末にまとめて計上しました");
    for (const k of KINDS) items.push(mkItem(monthEnd, k, unitOf[k], monthQty[k]));
  }

  // 控除（通常と同じ）
  const deductions = buildDeductions(driver, header.salesDate);
  items.push(...deductions);

  const { salesTotal, deductTotal, netTotal } = summarize(items);

  return {
    ...header,
    items,
    salesTotal,
    deductTotal,
    netTotal,
    warnings,
    specialRule: "佐川周南 特殊計上",
    status: uTakkyu === 0 ? "hold" : "draft",
  };
}

function mkItem(deliveryDate: string, name: string, unitPrice: number, quantity: number): ItemRow {
  return {
    deliveryDate,
    name,
    unitPrice,
    quantity,
    amount: unitPrice * quantity,
    taxRate: "10%",
    category: "sales",
    carrier: "sagawa",
  };
}
